import type { PropertyBoundary, PropertyMembership } from '@cht/shared'

/**
 * Which property the user was last working on, and that property's boundary.
 *
 * Both are persisted because the app must render offline: on a cold start with
 * no signal there is no way to fetch the boundary, and without a boundary there
 * is no map and no geofence. Framework-free so a native shell can reuse it.
 */
export interface KeyValueStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

const SELECTED_KEY = 'cht.selected-property'
const boundaryKey = (propertyId: string): string => `cht.boundary.${propertyId}`

const memoryStore = (): KeyValueStore => {
  const map = new Map<string, string>()
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => { map.set(key, value) },
    removeItem: (key) => { map.delete(key) }
  }
}

export class PropertyStore {
  constructor(private readonly storage: KeyValueStore = globalThis.localStorage ?? memoryStore()) {}

  /**
   * The property to open. Falls back to the first membership when the stored
   * choice is gone — a user removed from a property must not be stranded.
   */
  select(memberships: readonly PropertyMembership[]): PropertyMembership | undefined {
    if (memberships.length === 0) return undefined
    const stored = this.storage.getItem(SELECTED_KEY)
    return memberships.find((membership) => membership.id === stored) ?? memberships[0]
  }

  remember(propertyId: string): void {
    this.storage.setItem(SELECTED_KEY, propertyId)
  }

  cachedBoundary(propertyId: string): PropertyBoundary | undefined {
    const raw = this.storage.getItem(boundaryKey(propertyId))
    if (!raw) return undefined
    try { return JSON.parse(raw) as PropertyBoundary } catch { return undefined }
  }

  cacheBoundary(propertyId: string, boundary: PropertyBoundary): void {
    try { this.storage.setItem(boundaryKey(propertyId), JSON.stringify(boundary)) } catch { /* Quota is not fatal. */ }
  }
}

export const propertyStore = new PropertyStore()
